// Shared helpers for parsing and inspecting JSON values.

// Rendering very large JSON as deeply-nested tables can freeze the browser, so
// callers gate the table render once the node count crosses a limit.
export const DEFAULT_NODE_LIMIT = 20000;

// Count the nodes (every value, including nested) in a JSON-like value, but
// stop early once `limit` is exceeded so we never walk an enormous tree just to
// learn it is "too big". Returns the count (which may equal limit + 1 when capped).
export const countNodes = (value, limit = DEFAULT_NODE_LIMIT) => {
    let count = 0;
    const walk = (node) => {
        if (count > limit) return;
        count += 1;
        if (Array.isArray(node)) {
            for (let i = 0; i < node.length && count <= limit; i++) {
                walk(node[i]);
            }
        } else if (node !== null && typeof node === 'object') {
            const vals = Object.values(node);
            for (let i = 0; i < vals.length && count <= limit; i++) {
                walk(vals[i]);
            }
        }
    };
    walk(value);
    return count;
};

export const isTooLarge = (value, limit = DEFAULT_NODE_LIMIT) => {
    if (value === undefined || value === null) return false;
    return countNodes(value, limit) > limit;
};

// Human-readable type label used in the table headers.
export const valueType = (value) => {
    if (Array.isArray(value)) return 'Array';
    if (value === null) return 'Null';
    if (value === undefined) return 'Undefined';
    if (typeof value === 'object') return 'Object';
    return typeof value;
};

const sameKeys = (a, b) => {
    const aKeys = Object.keys(a).sort();
    const bKeys = Object.keys(b).sort();
    return JSON.stringify(aKeys) === JSON.stringify(bKeys);
};

// True when an array is non-empty and every element is a non-null object sharing
// the same set of keys — i.e. it can be rendered as a uniform grid with a header row.
export const isUniformObjectArray = (value) => {
    if (!Array.isArray(value) || value.length === 0) return false;
    let first;
    for (const item of value) {
        if (item === null || typeof item !== 'object' || Array.isArray(item)) return false;
        if (first === undefined) {
            first = item;
        } else if (!sameKeys(first, item)) {
            return false;
        }
    }
    return true;
};

// Parse text into JSON, returning { ok, value, error } instead of throwing.
export const safeParse = (text) => {
    try {
        return { ok: true, value: JSON.parse(text) };
    } catch (err) {
        return { ok: false, error: err.message };
    }
};

// Truncate a long primitive value for the collapsed (key-clicked) preview.
export const previewPrimitive = (value, max = 24) => {
    const str = String(value);
    return str.length > max ? str.slice(0, max) + '…' : str;
};
